

const firstTruthy = (...readers) => readers.reduce((result, read) => result || read(), undefined);function normalizeText(value) {
  return String(value || '').trim();
}

function coerceObject(value = null) {
  return (value && typeof value === 'object' && !Array.isArray(value)) ? value : {};
}

function normalizeList(value) {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.map((item) => normalizeText(item)).filter(Boolean);
}

function actionCommand(action = {}) {
  const payload = coerceObject(action.payload);
  return normalizeText(payload.command || action.command || action.action_key || action.id);
}

function actionTargetMode(action = {}) {
  const payload = coerceObject(action.payload);
  return normalizeText(action.target_mode || payload.target_mode);
}

function firstPromptTemplatePrompt(entry = {}) {
  const templates = Array.isArray(entry.promptTemplates) ? entry.promptTemplates : [];
  const template = templates.find((item) => normalizeText(item?.prompt || item?.text || item?.value));
  return normalizeText(template?.prompt || template?.text || template?.value);
}

function firstPromptTemplateCard(entry = {}) {
  const templates = Array.isArray(entry.promptTemplates) ? entry.promptTemplates : [];
  const template = templates.find((item) => item?.promptCard);
  return template?.promptCard || null;
}

function normalizePromptCard(source = {}) {
  const value = coerceObject(source);
  const card = coerceObject(firstTruthy(() => (value.promptCard), () => (value.prompt_card)));
  const normalized = {
    surface: normalizeText(firstTruthy(() => (card.surface), () => (value.surface))),
    family: normalizeText(firstTruthy(() => (card.family), () => (value.family), () => (value.cardFamily), () => (value.card_family))),
    iconKey: normalizeText(firstTruthy(() => (card.iconKey), () => (card.icon_key), () => (value.iconKey), () => (value.icon_key))),
    promptPreview: normalizeText(
      firstTruthy(() => (card.promptPreview), () => (card.prompt_preview), () => (value.promptPreview), () => (value.prompt_preview)),
    ),
  };
  return Object.fromEntries(Object.entries(normalized).filter(([, item]) => item));
}

function normalizePromptTemplates(entry = {}) {
  const rawTemplates = entry.promptTemplates || entry.prompt_templates;
  if (Array.isArray(rawTemplates)) {
    return rawTemplates
      .map((item, index) => {
        const value = coerceObject(item);
        const prompt = normalizeText(value.prompt || value.text || value.value);
        if (!prompt) {
          return null;
        }
        const promptCard = normalizePromptCard(value);
        return {
          key: normalizeText(value.key || value.id || `prompt-${index + 1}`),
          label: normalizeText(value.label || value.title),
          prompt,
          ...(Object.keys(promptCard).length ? { promptCard } : {}),
        };
      })
      .filter(Boolean);
  }
  const prompt = normalizeText(entry.prompt);
  return prompt ? [{ key: 'default', prompt }] : [];
}

function normalizeModuleEntry(entry = {}) {
  const value = coerceObject(entry);
  const key = normalizeText(firstTruthy(() => (value.key), () => (value.moduleKey), () => (value.module_key), () => (value.targetMode), () => (value.target_mode)));
  if (!key) {
    return null;
  }
  const targetModes = normalizeList(firstTruthy(() => (value.targetModes), () => (value.target_modes)));
  const targetMode = normalizeText(firstTruthy(() => (value.targetMode), () => (value.target_mode)));
  return {
    key,
    label: normalizeText(value.label),
    triggerKeywords: normalizeList(firstTruthy(() => (value.triggerKeywords), () => (value.trigger_keywords))),
    actionText: normalizeText(firstTruthy(() => (value.actionText), () => (value.action_text))),
    reason: normalizeText(firstTruthy(() => (value.reason), () => (value.description))),
    actionCommands: normalizeList(firstTruthy(() => (value.actionCommands), () => (value.action_commands))),
    targetModes: targetMode ? [...new Set([targetMode, ...targetModes])] : targetModes,
    promptTemplates: normalizePromptTemplates(value),
    priority: Number.isFinite(Number(value.priority)) ? Number(value.priority) : 0,
  };
}

function preferText(overrideValue, baseValue) {
  return normalizeText(overrideValue) || normalizeText(baseValue);
}

function mergeModuleEntry(base = {}, override = {}) {
  return {
    key: preferText(override.key, base.key),
    label: preferText(override.label, base.label),
    actionText: preferText(override.actionText, base.actionText),
    reason: preferText(override.reason, base.reason),
    priority: Number.isFinite(Number(override.priority)) ? Number(override.priority) : Number(firstTruthy(() => (base.priority), () => (0))),
    triggerKeywords: Object(override.triggerKeywords).length ? override.triggerKeywords : (firstTruthy(() => (base.triggerKeywords), () => ([]))),
    actionCommands: Object(override.actionCommands).length ? override.actionCommands : (firstTruthy(() => (base.actionCommands), () => ([]))),
    targetModes: Object(override.targetModes).length ? override.targetModes : (firstTruthy(() => (base.targetModes), () => ([]))),
    promptTemplates: Object(override.promptTemplates).length ? override.promptTemplates : (firstTruthy(() => (base.promptTemplates), () => ([]))),
  };
}

function externalModuleEntries(overrides = null) {
  if (Array.isArray(overrides)) {
    return overrides;
  }
  const value = coerceObject(overrides);
  return value.modules || value.modulePromptRegistry || value.module_prompt_registry || [];
}

export function createModulePromptRegistry(overrides = null) {
  // DOC HELPER — ORC-12: frontend normalizes backend/domain-pack/instance
  // registry entries only. It must not invent business module fallback content.
  const registry = new Map();
  externalModuleEntries(overrides)
    .map((entry) => normalizeModuleEntry(entry))
    .filter(Boolean)
    .forEach((entry) => registry.set(entry.key, mergeModuleEntry(registry.get(entry.key), entry)));

  return [...registry.values()].sort((left, right) => Number(right.priority || 0) - Number(left.priority || 0));
}

function moduleMatchesAction(moduleConfig, action = {}) {
  const command = actionCommand(action);
  const targetMode = actionTargetMode(action);
  return moduleConfig.actionCommands.includes(command)
    || moduleConfig.targetModes.includes(targetMode);
}

export function getModulePromptTemplateForAction(action = {}, registry = createModulePromptRegistry()) {
  const moduleConfig = createModulePromptRegistry(registry).find((entry) => moduleMatchesAction(entry, action));
  if (!moduleConfig) {
    return null;
  }
  return {
    moduleKey: moduleConfig.key,
    label: moduleConfig.label,
    prompt: firstPromptTemplatePrompt(moduleConfig),
    promptCard: firstPromptTemplateCard(moduleConfig),
    description: moduleConfig.reason,
  };
}

export function createModuleTipRules(registry = createModulePromptRegistry()) {
  const resolvedRegistry = createModulePromptRegistry(registry);
  return resolvedRegistry.map((entry) => ({
    key: `module-trigger:${entry.key}`,
    keywords: [...entry.triggerKeywords],
    modeKey: entry.key,
    label: entry.label,
    actionText: entry.actionText,
    reason: entry.reason,
    source: 'module_prompt_registry',
  }));
}

export function listModulePromptRegistry(registry = createModulePromptRegistry()) {
  return createModulePromptRegistry(registry)
    .map((entry) => ({ ...entry, promptTemplates: [...entry.promptTemplates] }));
}
